import logger from 'js-logger';

import { localStorageInstance } from '@gem/modules/storage';
import { parseJSON, platformInfo } from '@gem/utils';

import {
  CipherSuite,
  Client,
  CommitBuilderInterface,
  CommitOutput,
  generateSignatureKeypair,
  GroupInterface,
  messageFromBytes,
  messageToBytes,
  ReceivedMessage_Tags,
  SignatureKeypair,
  SigningIdentityInterface,
} from 'APP/libs/mls-rs-wasm';
import { IE2eeCommit, IE2eeWelcome, IUserDeviceKeyPackage } from 'APP/model/e2ee/e2eeModel.types';
import { isEqualArrayBuffers } from 'UTILS/e2ee/isEqualArrayBuffers';
import { transformArrayBufferToBase64 } from 'UTILS/e2ee/transformArrayBufferToBase64';
import { transformBase64ToArrayBuffer } from 'UTILS/e2ee/transformBase64ToArrayBuffer';

import { MlsGroupStateStorageMemory } from './MlsGroupStateStorageMemory';
import { MlsKeyPackageStorageMemory } from './MlsKeyPackageStorageMemory';
import {
  IAddMembersOutput,
  IRemoveMembersOutput,
  IStorageKeyPair,
  MLSErrors,
} from './MlsStorageService.types';

class MLSStorageServiceClass {
  private client: Client;
  private clientId: string | null = null;
  private groupStorage: MlsGroupStateStorageMemory;
  private keyPackageStorage: MlsKeyPackageStorageMemory;

  // актуальные методы
  createClient(
    groupStorage: MlsGroupStateStorageMemory,
    keyPackageStorage: MlsKeyPackageStorageMemory
  ): Client {
    if (!this.clientId) {
      throw new Error('clientId is required');
    }

    const bufferId = new TextEncoder().encode(this.clientId).buffer;
    let keyPair: SignatureKeypair;

    const keyPairJson = localStorageInstance.getItem(`keyPair${this.clientId}`);
    const keyPairJsonParsed = parseJSON<IStorageKeyPair>(keyPairJson || '', true);

    if (keyPairJsonParsed) {
      keyPair = {
        cipherSuite: Number(keyPairJsonParsed.cipherSuite),
        publicKey: { bytes: transformBase64ToArrayBuffer(keyPairJsonParsed.publicKey) },
        secretKey: { bytes: transformBase64ToArrayBuffer(keyPairJsonParsed.secretKey) },
      };
    } else {
      keyPair = generateSignatureKeypair(CipherSuite.Curve25519Aes128);

      const base64KeyPair = {
        cipherSuite: 0,
        publicKey: transformArrayBufferToBase64(keyPair.publicKey.bytes),
        secretKey: transformArrayBufferToBase64(keyPair.secretKey.bytes),
      };
      localStorageInstance.setItem(`keyPair${this.clientId}`, JSON.stringify(base64KeyPair));
    }

    this.groupStorage = groupStorage;
    this.keyPackageStorage = keyPackageStorage;

    return new Client(bufferId, keyPair, {
      useRatchetTreeExtension: true,
      groupStateStorage: groupStorage,
      keyPackageStorage: keyPackageStorage,
    });
  }

  clearStorages(): void {
    this.groupStorage?.clearStorage();
    this.keyPackageStorage?.clearStorage();
    localStorageInstance.removeItem(`keyPair${this.clientId}`);
    if (this.client) {
      this.client.uniffiDestroy();
      this.clientId = null;
    }
  }

  async initMlsGroupStorage(userId: string): Promise<void> {
    const deviceId = platformInfo.deviceId;
    this.clientId = `${userId}:${deviceId}`;
    const groupStorage = new MlsGroupStateStorageMemory(this.clientId);
    await groupStorage.init();
    const keyPackageStorage = new MlsKeyPackageStorageMemory(this.clientId);
    await keyPackageStorage.init();
    this.client = this.createClient(groupStorage, keyPackageStorage);
  }

  getKeyPackageByteArray(): ArrayBuffer {
    const client2keyPackage = this.client.generateKeyPackageMessage();

    return messageToBytes(client2keyPackage);
  }

  getKeyPackagesCount(): number {
    return this.keyPackageStorage.getCount();
  }

  addMembersToGroup(
    keyPackages: IUserDeviceKeyPackage[],
    mlsGroup: GroupInterface,
    commitBuilder?: CommitBuilderInterface
  ): IAddMembersOutput {
    // достаем только сами пекеджи
    const clientKeyPackages = keyPackages.map((item) => messageFromBytes(item.keyPackage!.data));

    let welcomeCommit: CommitOutput;

    if (commitBuilder) {
      commitBuilder.addMembers(clientKeyPackages);
      welcomeCommit = commitBuilder.build();
    } else {
      // коммитим, забираем велкомы, пишемся в сторадж
      welcomeCommit = mlsGroup.addMembers(clientKeyPackages);
    }

    // распределяем велком месседжи между юзерами
    const welcomeMessagesToDeviceId = welcomeCommit.welcomeMessages
      .map((item) => {
        // получаем референсы на кей пекеджи из велком месседжей
        const keyPackagesReferences = item.welcomeKeyPackageReferences();
        const epoch = item.epoch();
        // поиском с побитовым сравнением кей пекеджев между референсом из велкома
        // и входяжего массива кей пекеджев находим кей пекедж конкретного юзера,
        // чтобы забрать его айдишники и присвоить велком месседжу
        const targetUserDevice = keyPackages.find((item) => {
          const itemKeyPackage = messageFromBytes(item.keyPackage!.data);
          const reference = itemKeyPackage.keyPackageReference(CipherSuite.Curve25519Aes128);

          return reference ? isEqualArrayBuffers(keyPackagesReferences[0], reference) : false;
        });

        if (!targetUserDevice) {
          return null;
        }

        return {
          epoch: epoch || welcomeCommit.commitMessage.epoch()!,
          deviceId: targetUserDevice.deviceId,
          userId: targetUserDevice.userId,
          payload: new Uint8Array(messageToBytes(item)),
        };
      })
      .filter((item) => !!item);

    return {
      commit: messageToBytes(welcomeCommit.commitMessage),
      welcomeMessages: welcomeMessagesToDeviceId,
      epoch: welcomeCommit.commitMessage.epoch()!,
      welcomeCommit,
    };
  }

  findMembersIdsForRemove(ids: string[], group: GroupInterface): SigningIdentityInterface[] {
    group.writeToStorage();
    const groupMembers = group.members();

    return groupMembers
      .filter((member) => {
        const memberClientId = new TextDecoder().decode(member.identifier);
        let isIncludes = false;

        ids.forEach((id) => {
          const [userId, deviceId] = memberClientId.split(':');
          if (userId === id || deviceId === id) {
            isIncludes = true;
          }
        });

        return isIncludes;
      })
      .map((member) => member.signingIdentity);
  }

  findMemberIdsInGroupAndRemove(
    ids: string[],
    group: GroupInterface
  ): CommitBuilderInterface | null {
    const groupMembersForRemove = this.findMembersIdsForRemove(ids, group);

    if (!groupMembersForRemove.length) {
      return null;
    }
    const commitBuilder = group.commitBuilder();
    commitBuilder.removeMembers(groupMembersForRemove);

    return commitBuilder;
  }

  removeMembersByIdOrDeviceId(ids: string[], group: GroupInterface): IRemoveMembersOutput | null {
    group.writeToStorage();
    const groupMembersForRemove = this.findMembersIdsForRemove(ids, group);

    if (!groupMembersForRemove.length) {
      return null;
    }

    const commitOutput = group.removeMembers(groupMembersForRemove);

    return {
      commit: messageToBytes(commitOutput.commitMessage),
      epoch: commitOutput.commitMessage.epoch()!,
      commitOutput,
    };
  }

  processMyWelcomeCommit(welcomeCommit: CommitOutput, mlsGroup: GroupInterface): void {
    mlsGroup.processIncomingMessage(welcomeCommit.commitMessage);
    mlsGroup.writeToStorage();
  }

  createGroup(uuid: string): GroupInterface {
    const group = this.client.createGroup(new TextEncoder().encode(uuid));
    group.writeToStorage();
    return group;
  }

  deleteGroup(id: string): void {
    const groupUuid = this.getGroupUuidById(id);
    if (!groupUuid) {
      return;
    }
    this.groupStorage.deleteGroupFromStorage(groupUuid);
    const groupIdUuidChainStorage = localStorageInstance.getItem(
      `groupIdUuidChain${this.clientId}`
    );

    const groupIdUuidChainMap = parseJSON<Record<string, string>>(
      groupIdUuidChainStorage || '',
      true
    );

    if (!groupIdUuidChainMap) {
      return;
    }
    delete groupIdUuidChainMap[id];

    localStorageInstance.setItem(
      `groupIdUuidChain${this.clientId}`,
      JSON.stringify(groupIdUuidChainMap)
    );
  }

  chainGroupIdAndUuid(id: string, uuid: string): void {
    const groupIdUuidChainStorage = localStorageInstance.getItem(
      `groupIdUuidChain${this.clientId}`
    );
    let groupIdUuidChainMap = parseJSON<{ [key: string]: string }>(
      groupIdUuidChainStorage || '',
      true
    );

    if (!groupIdUuidChainMap) {
      groupIdUuidChainMap = {
        [id]: uuid,
      };
    } else {
      groupIdUuidChainMap[id] = uuid;
    }
    localStorageInstance.setItem(
      `groupIdUuidChain${this.clientId}`,
      JSON.stringify(groupIdUuidChainMap)
    );
  }

  getGroupUuidById(id: string): string | undefined {
    const groupIdUuidChainStorage = localStorageInstance.getItem(
      `groupIdUuidChain${this.clientId}`
    );
    const groupIdUuidChainMap = parseJSON<{ [key: string]: string }>(
      groupIdUuidChainStorage || '',
      true
    );

    if (!groupIdUuidChainMap) {
      return;
    }

    return groupIdUuidChainMap[id];
  }

  loadGroup(id: string): GroupInterface | null {
    try {
      const groupUuid = this.getGroupUuidById(id);

      if (!groupUuid) {
        return null;
      }
      const group = this.client.loadGroup(new TextEncoder().encode(groupUuid));
      group.writeToStorage();
      return group;
    } catch (e) {
      return null;
    }
  }

  processWelcomeMessage(welcomeMessage: IE2eeWelcome): boolean {
    try {
      const alreadyExistsGroup = this.loadGroup(welcomeMessage.chatId);

      if (alreadyExistsGroup) {
        return true;
      }
      const message = messageFromBytes(welcomeMessage.payload);
      const mlsGroup = this.client.joinGroup(undefined, message).group;
      mlsGroup.writeToStorage();
      const groupUuid = mlsGroup.groupId();
      this.chainGroupIdAndUuid(welcomeMessage.chatId, new TextDecoder('utf-8').decode(groupUuid));

      return true;
    } catch (e) {
      logger.get('MLSStorage').error('MLSStorage.processWelcomeMessage', e);
      return false;
    }
  }

  processCommitMessage(commitMessage: IE2eeCommit, groupId: string): void {
    try {
      const message = messageFromBytes(commitMessage.payload);

      const mlsGroup = this.loadGroup(groupId);

      if (!mlsGroup) {
        return;
      }

      mlsGroup.processIncomingMessage(message);
      mlsGroup.writeToStorage();
    } catch (e) {
      logger.get('MLSStorage').error('MLSStorage.processCommitMessage', e);
    }
  }

  encodeMessage(message: ArrayBuffer, groupId: string): ArrayBuffer | null {
    try {
      const groupUuid = this.getGroupUuidById(groupId);
      const mlsGroup = this.client.loadGroup(new TextEncoder().encode(groupUuid));
      mlsGroup.writeToStorage();

      if (!mlsGroup) {
        return null;
      }

      const mlsMessage = mlsGroup.encryptApplicationMessage(message);
      mlsGroup.writeToStorage();
      return messageToBytes(mlsMessage);
    } catch (e) {
      logger.get('MLSStorage').error('MLSStorage.encodeMessage', e);
      return null;
    }
  }

  parseMessage(message: ArrayBuffer, groupId: string, withEpochId?: boolean): ArrayBuffer | null {
    try {
      const groupUuid = this.getGroupUuidById(groupId);
      const messageInterface = messageFromBytes(message);
      let mlsGroup: GroupInterface;

      if (!withEpochId) {
        mlsGroup = this.client.loadGroup(new TextEncoder().encode(groupUuid));
        mlsGroup.writeToStorage();
      } else {
        mlsGroup = this.client.loadGroup(
          // Актуальная эпоха сообщения соответствует эпохе группы - 1
          new TextEncoder().encode(`${groupUuid}:${Number(messageInterface.epoch()) - 1}`)
        );
      }

      if (!mlsGroup) {
        return null;
      }

      const incomingMessage = mlsGroup.processIncomingMessage(messageInterface);
      if (incomingMessage.tag === ReceivedMessage_Tags.ApplicationMessage) {
        return incomingMessage.inner.data;
      }
      return null;
    } catch (e) {
      if (withEpochId) {
        if (e.message === MLSErrors.groupNotFound) {
          return null;
        }
        logger.get('MLSStorage').error('MLSStorage.parseMessage', e);
        return null;
      } else {
        return this.parseMessage(message, groupId, true);
      }
    }
  }
}

export const MlsStorageService = new MLSStorageServiceClass();
